/**
 * Service de mapping temporaire entre les plans statiques et les products de la base de données
 * Ce service permet une transition progressive vers l'utilisation complète de la table products
 */

import { supabase } from "@/integrations/supabase/client";
import type { Product } from "./products";

/**
 * Plans statiques actuellement utilisés dans l'UI
 * À terme, ces plans seront remplacés par des données de la table products
 */
const STATIC_PLANS = {
  presence: { slug: 'presence', duration: 210 }, // 3h30
  emergence: { slug: 'emergence', duration: 180 }, // 3h
  acceleration: { slug: 'acceleration', duration: 180 }, // 3h
  'presence-multihook': { slug: 'presence-multihook', duration: 210 }, // 3h30
  'emergence-multihook': { slug: 'emergence-multihook', duration: 180 }, // 3h
  'acceleration-multihook': { slug: 'acceleration-multihook', duration: 180 }, // 3h
} as const;

type PlanValue = keyof typeof STATIC_PLANS;

/**
 * Récupère le product correspondant à un plan statique
 * @param planValue - Valeur du plan (ex: 'presence', 'emergence', 'acceleration')
 * @returns Product de la base de données ou null
 */
export async function getProductFromPlan(planValue: string): Promise<Product | null> {
  const planConfig = STATIC_PLANS[planValue as PlanValue];
  
  if (!planConfig) {
    console.error(`❌ Plan inconnu: ${planValue}`);
    return null;
  }

  try {
    const { data: product, error } = await supabase
      .from('products')
      .select('*')
      .eq('slug', planConfig.slug)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    
    if (!product) {
      console.warn(`⚠️ Aucun produit trouvé pour le plan ${planValue} (slug: ${planConfig.slug})`);
    }
    
    return product;
  } catch (error) {
    console.error(`❌ Erreur lors de la récupération du product pour ${planValue}:`, error);
    return null;
  }
}

/**
 * Vérifie si un plan statique existe
 */
export function isValidPlan(planValue: string): planValue is PlanValue {
  return planValue in STATIC_PLANS;
}

/**
 * Récupère les informations d'un plan statique
 */
export function getStaticPlanInfo(planValue: string) {
  return STATIC_PLANS[planValue as PlanValue] || null;
}
